import React from 'react';
import { get, toJs, isEmpty, map } from 'mori';
import Wahanda from 'common/wahanda';
import ReactDialog from 'src/components/common/react-dialog';
import OverviewView from './transaction-summary/overview';
import SummaryRowsView from './transaction-summary/row';
import Pricing from './transaction-summary/pricing';
import { getByType } from './utils';

const DIALOG_WIDTH = 470;

type MoriData = object;

type LineItemCollectionKey =
  | 'serviceLineItems'
  | 'cancelledServiceLineItems'
  | 'productLineItems'
  | 'cancelledProductLineItems';

type TransactionSummaryDialogProps = {
  data?: MoriData;
  onClose?: () => void;
  type?: 'cancellation';
  dialogTitle?: string | {};
  footerContent?: React.ReactNode;
  linkedTransaction?: React.ReactNode;
};

const getFooter = (footerContent?: React.ReactNode): React.ReactNode => {
  if (footerContent) {
    return <div className="footer">{footerContent}</div>;
  }
  return null;
};

const getFirst = (hMap: unknown, try1: string, try2: string): string | undefined => {
  const first = get(hMap, try1);
  return first || get(hMap, try2);
};

const getLineItemRows = (
  isCancellation: boolean,
  data: MoriData,
  checkoutKey: LineItemCollectionKey,
  cancellationKey: LineItemCollectionKey,
  renderItem: (item: unknown) => React.ReactElement,
): React.ReactElement[] => {
  const items = get(data, getByType(isCancellation, checkoutKey, cancellationKey));
  if (isEmpty(items)) {
    return [];
  }
  return toJs(map(renderItem, items));
};

const getServiceRows = (isCancellation: boolean, data: MoriData): React.ReactElement[] =>
  getLineItemRows(
    isCancellation,
    data,
    'serviceLineItems',
    'cancelledServiceLineItems',
    (service) => (
      <SummaryRowsView
        key={get(service, 'id')}
        name={getFirst(service, 'name', 'description')}
        totalPrice={get(service, 'amount')}
        constituents={get(service, 'serviceLineItemConstituents')}
        negative={isCancellation}
      />
    ),
  );

const getProductRows = (isCancellation: boolean, data: MoriData): React.ReactElement[] =>
  getLineItemRows(
    isCancellation,
    data,
    'productLineItems',
    'cancelledProductLineItems',
    (product) => (
      <SummaryRowsView
        key={get(product, 'id')}
        name={getFirst(product, 'name', 'description')}
        totalPrice={get(product, 'amount')}
        quantity={get(product, 'quantity')}
        unitPrice={get(product, 'unitPrice')}
        negative={isCancellation}
      />
    ),
  );

const getServices = (isCancellation: boolean, data: MoriData): React.ReactElement | null => {
  const serviceRows = getServiceRows(isCancellation, data);
  if (serviceRows.length === 0) {
    return null;
  }
  return <div className="service-section">{serviceRows}</div>;
};

const getProducts = (isCancellation: boolean, data: MoriData): React.ReactElement | null => {
  const productRows = getProductRows(isCancellation, data);
  if (productRows.length === 0) {
    return null;
  }
  return <div className="product-section">{productRows}</div>;
};

export default class TransactionSummaryDialog extends React.Component<
  TransactionSummaryDialogProps
> {
  render() {
    const { data, linkedTransaction, footerContent, dialogTitle, onClose, type } = this.props;
    const isCancellation = type === 'cancellation';
    const lang = Wahanda.lang.reports.transactions.checkoutSummary;

    let content: React.ReactNode;
    if (!data) {
      content = <div className="loading">{lang.loading}</div>;
    } else {
      const serviceList = getServices(isCancellation, data);
      const productList = getProducts(isCancellation, data);
      const maybeSeparator = productList && serviceList ? <span className="separator" /> : null;
      // For Refunds, change some strings
      const langPart = Wahanda.lang.reports.transactions.refundSummary;
      const overviewLang = isCancellation
        ? {
            ...lang,
            transactionLabel: langPart.refundIdLabel,
            checkoutOutLabel: langPart.refundedByLabel,
          }
        : lang;

      content = (
        <div>
          {linkedTransaction}
          <OverviewView data={data} lang={overviewLang} isCancellation={isCancellation} />
          {serviceList}
          {maybeSeparator}
          {productList}
          <Pricing isCancellation={isCancellation} data={data} lang={lang} />
          {getFooter(footerContent)}
        </div>
      );
    }

    return (
      <ReactDialog
        dataTest="report-transaction-summary-modal"
        title={dialogTitle}
        classes={{ 'checkout-summary': true }}
        width={DIALOG_WIDTH}
        onClose={onClose}
      >
        {content}
      </ReactDialog>
    );
  }
}
